import { useCallback, useContext, useEffect, useState, ChangeEvent } from 'react'

import { Text, TextField, Button } from '@cash-me/react-components'
import { cpf, cnpj } from 'cpf-cnpj-validator'

import { billsClient } from 'src/adapters/http'
import { formatCPFNumber, formatCNPJNumber } from 'src/utils/formatter'
import { pushDataLayer } from 'utils/push-data-layer'

import { DuplicateBillContext } from '..'
import type { FormValidation, DuplicateBillContextProps } from '../types'

import * as S from './styles'

type FormErrors = {
  document?: FormValidation
  contract?: FormValidation
}

export const DocumentForm = () => {
  const { fields, changeCallback, routeCallback } = useContext<DuplicateBillContextProps>(DuplicateBillContext)
  const [validations, setValidations] = useState<FormErrors>({ document: {}, contract: {} })
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleClickUnknownInfo = () => {
    pushDataLayer({
      category: 'Clique',
      action: 'Sou cliente',
      label: `Link Não sei o número do contrato`
    })
    routeCallback('info', { type: 'no-info' })
  }

  const formatDocument = useCallback(
    (document: string) =>
      /[a-zA-Z]/.test(document) || document.length > 11
        ? formatCNPJNumber(document)
        : formatCPFNumber(document),
    []
  )

  const handleDocumentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, textfieldValue?: string) => {
      const cleanValue = textfieldValue?.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() ?? ''
      changeCallback('document', cleanValue)
    },
    [changeCallback]
  )

  const handleContractChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, textfieldValue?: string) => {
      const contract = textfieldValue?.replace(/[^\d\w\/\-\.]/g, '') ?? ''
      changeCallback('contract', contract)
    },
    [changeCallback]
  )

  const validateFields = useCallback(() => {
    const newErrors: FormErrors = { document: { valid: '' }, contract: { valid: '' } }

    if (!fields.document) newErrors['document'] = { invalid: 'Preencha este campo para continuar.' }
    if (!fields.contract) newErrors['contract'] = { invalid: 'Preencha este campo para continuar.' }

    setValidations(newErrors)
  }, [fields])

  const handleSubmit = useCallback(async () => {
    validateFields()
    if (!(validations.document?.valid === '' && validations.contract?.valid === '')) return
    if (!cpf.isValid(fields.document) && !cnpj.isValid(fields.document)) {
      setValidations({ document: { invalid: 'Verifique se o CPF/CNPJ digitado está correto.' } })
      return
    }

    setIsLoading(true)

    try {
      const { data } = await billsClient.getMFAOptions(fields.contract, fields.document)

      changeCallback('mfaOptions', data.canaisMfa)
      routeCallback('mfa-options')
    } catch (e) {
      const status = (e as { response: { status: number } }).response?.status
      if (status < 500) {
        routeCallback('info', {
          type: 'wrong-info'
        })
      } else {
        routeCallback('info', {
          type: 'server-error'
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [changeCallback, fields, routeCallback, validateFields, validations])

  useEffect(() => {
    if (fields.document || fields.contract) validateFields()
  }, [fields, validateFields])

  return (
    <S.Form>
      <Text size="heading5" weight="medium" color="var(--colors-neutral-text-default)">
        Por favor, informe os dados abaixo
      </Text>
      <S.FieldWrapper>
        <TextField
          id="document"
          name="document"
          label="CPF/CNPJ"
          value={formatDocument(fields.document)}
          validation={validations.document}
          variant="outline"
          onChange={handleDocumentChange}
          maxLength={18}
          hideWordCount
          required
        />
        <TextField
          id="contract"
          name="contract"
          label="Número do contrato"
          value={fields.contract}
          validation={validations.contract}
          variant="outline"
          onChange={handleContractChange}
          hideWordCount
          required
        />
        <S.Link onClick={() => handleClickUnknownInfo()}>
          <Text as="span" size="body2" color="var(--colors-blue-text-default)">
            Não sei o número do contrato
          </Text>
        </S.Link>
      </S.FieldWrapper>

      <Text size="caption" color="var(--colors-neutral-text-weak)">
        Utilizaremos seus dados pessoais para atendermos a sua solicitação. Para maiores informações sobre como tratamos
        seus dados, acesse nosso{' '}
        <S.Link href="https://www.cashme.com.br/aviso-de-privacidade/">
          <Text as="span" size="caption" color="var(--colors-blue-text-default)">
            Aviso de Privacidade
          </Text>
        </S.Link>
        .
      </Text>
      <Button size="lg" type="button" variants="text-only" onClick={handleSubmit} loading={isLoading}>
        Enviar
      </Button>
    </S.Form>
  )
}
